/* What each Configuration section is for — the one-line bubble on hover of its ⓘ,
   and the dialog a click on that ⓘ opens. The copy lives here so the sections
   hold layout and the words have one home. */
import { Info } from "lucide-react";

import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/shared/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/components/ui/tooltip";

export type ConfigSection =
  | "model" | "instructions" | "guardrails" | "subagents" | "apps"
  | "mcp" | "skills" | "sandbox" | "web" | "approval" | "kb" | "voice";

interface SectionHelp {
  title: string;
  /** The hover bubble, and the dialog's subtitle. */
  summary: string;
  details: string[];
  /** What the agent loses with the section's switch off — toggleable sections only. */
  off?: string;
}

export const SECTION_HELP: Record<ConfigSection, SectionHelp> = {
  model: {
    title: "Model",
    summary: "The language model that reads, reasons and writes for this agent.",
    details: [
      "Pick the model that runs this agent and the key it runs on — one of Definable's own models or one of your own provider connections.",
      "Model parameters tune its replies: temperature sets how varied they are (lower is more focused, higher more creative), and max tokens caps how long a single reply can be.",
      "Add a fast model to save on simple messages: Definable sends each new message to the model whose strengths fit it, quick lookups and small talk to the fast one, and either model can hand the rest of a reply to the other.",
      "A different model changes quality, speed and cost. Publish to make new conversations use it.",
    ],
  },
  instructions: {
    title: "Instructions",
    summary: "The system prompt: who the agent is and how it should work.",
    details: [
      "Every conversation starts with these instructions. Describe the agent's role and tone, what it should and shouldn't do, and the steps it should follow.",
      "Be specific: name the tools it should reach for, the format answers should take, and when it should ask before acting.",
      "Edits save to the draft. Publish to make new conversations use them.",
    ],
  },
  guardrails: {
    title: "Guardrails",
    summary: "Short rules the agent must never break.",
    details: [
      "The agent reads these rules last, before every reply and every step of its work, so they come after anything a person writes or a tool returns — an email or a web page that tells it to do otherwise included.",
      "Write one rule per line, as a plain sentence: \"Never promise a refund.\" When a request would break one, the agent is told to decline that part without quoting the rule.",
      "The model is asked to follow them; nothing checks its replies. Prove the ones that matter with an eval. Edits save to the draft: publish to make new conversations use them.",
    ],
  },
  subagents: {
    title: "Sub-agents",
    summary: "Lets the agent create helpers to work on parts of a task in parallel.",
    details: [
      "For a large task the agent can create sub-agents, each with its own instructions, hand them a piece of the work and combine what they return.",
      "The agent creates and manages them itself, so this list is read-only. To change how it delegates, edit its instructions.",
    ],
    off: "The agent handles every task itself, one step at a time.",
  },
  apps: {
    title: "Apps",
    summary: "Third-party apps the agent can work in, like Gmail, Slack or GitHub.",
    details: [
      "Add an app and the agent can read and act in it — send an email, post a message, open an issue.",
      "Each app uses your account, included for everyone who talks to the agent, or each customer's own, which they connect on the agent's page the first time it's needed. Switch it from the app's menu.",
      "Your accounts act with the permissions you granted when you connected them, and can be removed at any time.",
    ],
    off: "The agent can't use any app, even while accounts stay connected.",
  },
  mcp: {
    title: "MCP servers",
    summary: "Custom tool servers that speak the Model Context Protocol.",
    details: [
      "Add a remote server by URL — HTTP or SSE — to give the agent tools that aren't in the app catalog: internal APIs, databases, your own services. It can need no auth, a token, an API key, or an OAuth sign-in.",
      "A server is tested before it is saved, and belongs to the workspace: add it once, then put it on any agent. Clicking a server switches its tools off or on for this agent only.",
    ],
    off: "Tools from these servers are hidden from the agent.",
  },
  skills: {
    title: "Skills",
    summary: "Instruction packs the agent loads when a task calls for them.",
    details: [
      "A skill teaches a procedure — writing a release note, triaging a bug, reconciling an invoice.",
      "The agent loads a skill only when a task matches it, so adding skills doesn't crowd every chat.",
      "Skills come from public repos, so one person says yes: the agent's owner adds a skill after seeing what its security auditors found. An admin asks, and the owner approves.",
      "A skill's scripts run only with the sandbox on; without it the agent gets the instructions only. Attaching or removing one changes the draft: publish to let chats use it.",
    ],
  },
  sandbox: {
    title: "Sandbox",
    summary: "A private Linux computer, one per chat, where the agent runs code and works with files.",
    details: [
      "The agent can run shell commands, write and edit files, search them, start a server and share a preview link, open files the user attaches, and hand files back as downloads.",
      "Each chat gets its own machine, so one person's files never reach another's. It is kept between messages: it stops when idle and starts again with everything on it, until 30 days after its last use.",
      "Every machine starts from the agent's environment: ask Builder to install what its chats need, once. Secrets reach your team's chats and scheduled tasks as environment variables, never people on channels or the API.",
      "With the team machine on, your team's chats and scheduled tasks share one machine instead.",
    ],
    off: "The agent can't run code or open attached files.",
  },
  web: {
    title: "Web",
    summary: "Search the web and read pages, so the agent can answer with what's online now.",
    details: [
      "web_search finds ten results for a query, each with a title, link and snippet. When the snippets aren't enough, the same call also reads the top pages, which saves a step.",
      "web_fetch reads pages by link, up to ten at once: a link someone sends, a result it wants whole, a docs page it already knows.",
      "Both only read. Pages behind a sign-in, a bot check or a paywall stay out of reach, and the agent says which ones it couldn't read.",
      "On for every agent. Switch it off for an agent that must answer only from its own knowledge and apps, then publish.",
    ],
    off: "The agent can't search the web or read pages.",
  },
  voice: {
    title: "Voice",
    summary: "Calls: people talk with the agent in the Definable app and on the web, and it rings them when something is worth a call.",
    details: [
      "A Call button opens a call from its chat. It rings for a moment, then the agent picks up and talks: quick turns of a few sentences, every word from the agent's own model, tools and knowledge.",
      "With calls on, the agent can ring the person it's talking with (call_person) — when news they're waiting on arrives, or something needs their say. If they don't pick up, its reply in the chat says why it called.",
      "Each turn lands in the person's chat with the agent, so the call's transcript is the conversation. Approvals are asked aloud and a yes or no answers them.",
      "Pick the voice it talks in and the models that hear and speak for it. Publish to make calls use the change.",
    ],
    off: "Nobody can call the agent, and it can't ring anyone.",
  },
  approval: {
    title: "Needs approval",
    summary: "Tools and app actions a person approves before each call.",
    details: [
      "Before a call to anything on this list, the run stops and waits. Whoever is talking to the agent sees what it wants to run, with its arguments, and approves or denies it — in this app, or on the channel they wrote from.",
      "Approved, the call runs as it was asked. Denied, it doesn't run, and the agent is told so and carries on.",
      "Pick an app's actions one by one (Send email, Delete file…), every app action at once, or a sandbox tool. Publish to make new conversations ask.",
    ],
  },
  kb: {
    title: "Knowledge",
    summary: "The knowledge bases this agent's chats search before answering.",
    details: [
      "Knowledge bases are indexed collections of your documents. The agent searches them to ground its answers in your own material.",
      "A chat follows the agent's knowledge bases until you pick its own. Change them for this chat only, or reset to the agent's.",
    ],
    off: "The agent answers this chat from its instructions and the conversation alone.",
  },
};

/** The ⓘ beside a section title. A span, not a button: it sits inside the
    accordion's trigger <button>, and it stops its click from toggling it. */
export function SectionInfoButton({ section, onOpen }: {
  section: ConfigSection;
  onOpen: (section: ConfigSection) => void;
}) {
  const { title, summary } = SECTION_HELP[section];
  const open = (e: React.SyntheticEvent) => { e.preventDefault(); e.stopPropagation(); onOpen(section); };
  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="button"
            tabIndex={0}
            aria-label={`About ${title}`}
            onClick={open}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") open(e); }}
            className="press shrink-0 w-5 h-5 -my-1 rounded-full flex items-center justify-center cursor-pointer text-[var(--text-quaternary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
          >
            <Info className="w-3 h-3" strokeWidth={2} />
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[240px] rounded-[8px] bg-[var(--brand)] text-[var(--brand-foreground)] px-2.5 py-1.5 text-[12.5px] leading-[1.45] tracking-[-0.005em] shadow-menu"
        >
          {summary}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function SectionHelpDialog({ section, onOpenChange }: {
  section: ConfigSection | null;
  onOpenChange: (open: boolean) => void;
}) {
  const help = section && SECTION_HELP[section];
  return (
    <Dialog open={!!help} onOpenChange={onOpenChange}>
      {help && (
        <DialogContent className="p-0 gap-0 w-[440px] max-w-[94vw] rounded-[16px] overflow-hidden bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog">
          <div className="px-5 pt-5 pb-4 pr-12">
            <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{help.title}</DialogTitle>
            <DialogDescription className="text-[13px] text-[var(--text-tertiary)] mt-0.5">{help.summary}</DialogDescription>
          </div>
          <div className="px-5 pb-5 space-y-2.5">
            {help.details.map((line) => (
              <p key={line} className="text-[13.5px] leading-[1.65] text-[var(--text-secondary)] tracking-[-0.005em]">{line}</p>
            ))}
          </div>
          {help.off && (
            <div className="px-5 py-3.5 border-t hairline bg-[var(--surface)]">
              <div className="section-label mb-1">When it's off</div>
              <div className="text-[13px] leading-[1.55] text-[var(--text-secondary)] tracking-[-0.005em]">{help.off}</div>
            </div>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
}
